import React, { useEffect, useState } from 'react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, Target, BarChart3, 
  Calendar, ShieldAlert, Lock, RefreshCcw, ArrowUpRight 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../api/api';

export const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/summary');
      setData(res.data);
    } catch (error) {
      console.error("Failed to load report", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <RefreshCcw className="animate-spin text-blue-500" size={40} />
        <p className="text-slate-400 font-bold animate-pulse uppercase tracking-widest text-xs">
          {t('platform.reports.loading')}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-8 max-w-[1600px] mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter flex items-center gap-3">
            <BarChart3 className="text-blue-500" size={32} /> {t('platform.reports.title')}
          </h2>
          <p className="text-slate-500 text-sm font-medium mt-1">{t('platform.reports.desc')}</p>
        </div>
        <button 
          onClick={fetchReport}
          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-all flex items-center gap-2 border border-slate-700"
        >
          <RefreshCcw size={14} /> {t('platform.reports.refresh')}
        </button>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: t('platform.reports.profit'), value: `$${data.totalProfit.toFixed(2)}`, color: data.totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400', icon: <TrendingUp size={20}/> },
          { label: t('platform.reports.winrate'), value: `${data.winRate.toFixed(1)}%`, color: 'text-blue-400', icon: <Target size={20}/> },
          { label: t('platform.reports.totalTrades'), value: data.totalTrades, color: 'text-white', icon: <BarChart3 size={20}/> },
          { label: t('platform.reports.activeBans'), value: data.blacklisted?.length || 0, color: 'text-orange-400', icon: <ShieldAlert size={20}/> },
        ].map((card, i) => (
          <div key={i} className="bg-[#1e293b]/50 backdrop-blur-sm p-6 rounded-[24px] border border-slate-800 shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest">{card.label}</span>
              <div className="p-2 bg-slate-900 rounded-lg text-slate-400">{card.icon}</div>
            </div>
            <h3 className={`text-2xl font-black ${card.color}`}>{card.value}</h3>
          </div>
        ))}
      </div>

      {/* EQUITY CURVE CHART */}
      <div className="bg-[#1e293b]/50 backdrop-blur-sm p-8 rounded-[32px] border border-slate-800 shadow-2xl">
        <h4 className="text-white font-bold mb-8 flex items-center gap-2 uppercase text-xs tracking-[0.2em]">
            <Calendar size={14} className="text-blue-500" /> {t('platform.reports.chartTitle')}
        </h4>
        <div className="h-[350px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.dailyPnL}>
              <defs>
                <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} opacity={0.5} />
              <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} dy={10} />
              <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(val) => `$${val}`} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '16px', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' }}
                itemStyle={{ color: '#fff', fontWeight: 'bold' }}
              />
              <Area type="monotone" dataKey="profit" stroke="#3b82f6" strokeWidth={4} fillOpacity={1} fill="url(#colorProfit)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* DRIVERS VS VAMPIRES */}
          <div className="bg-[#1e293b]/50 p-6 rounded-[32px] border border-slate-800">
            <h4 className="text-white font-bold mb-6 uppercase text-xs tracking-widest flex items-center gap-2">
                <ArrowUpRight size={16} className="text-emerald-400" /> {t('platform.reports.performanceTitle')}
            </h4>
            <div className="space-y-3">
                {data.bySymbol.slice(0, 8).map((coin: any) => (
                    <div key={coin.symbol} className="group flex justify-between items-center p-4 bg-slate-950/40 hover:bg-slate-900/60 rounded-2xl transition-all border border-white/5">
                        <div className="flex items-center gap-3">
                           <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs ${coin.profit >= 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                             {coin.symbol.substring(0, 2)}
                           </div>
                           <div>
                              <p className="text-white font-black">{coin.symbol}</p>
                              <p className="text-[10px] text-slate-500">{t('platform.reports.tradesCount', { count: coin.count })}</p>
                           </div>
                        </div>
                        <span className={`font-mono font-bold ${coin.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {coin.profit >= 0 ? '+' : ''}{coin.profit.toFixed(2)} USDT
                        </span>
                    </div>
                ))}
            </div>
          </div>

          {/* ACTIVE BLACKLIST */}
          <div className="bg-[#1e293b]/50 p-6 rounded-[32px] border border-red-500/10">
            <h4 className="text-red-400 font-bold mb-6 uppercase text-xs tracking-widest flex items-center gap-2">
                <ShieldAlert size={16} /> {t('platform.reports.blacklistTitle')}
            </h4>
            <div className="space-y-3">
                {data.blacklisted?.length > 0 ? data.blacklisted.map((item: any) => (
                    <div key={item.id} className="p-4 bg-red-500/5 border border-red-500/10 rounded-2xl flex justify-between items-center group hover:bg-red-500/10 transition-all">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-red-500/20 rounded-xl text-red-500">
                                <Lock size={18} />
                            </div>
                            <div>
                                <p className="text-white font-black uppercase">{item.symbol}</p>
                                <p className="text-[10px] text-red-400/60 font-medium tracking-tight">
                                    {t('platform.reports.bannedUntil')} {new Date(item.until).toLocaleString()}
                                </p>
                            </div>
                        </div>
                        <div className="px-3 py-1 bg-red-500/20 rounded-full text-[10px] text-red-400 font-black uppercase animate-pulse">
                            {t('platform.reports.blockedLabel')}
                        </div>
                    </div>
                )) : (
                    <div className="h-full flex flex-col items-center justify-center py-12 text-slate-600">
                       <ShieldAlert size={48} className="opacity-10 mb-2" />
                       <p className="text-xs italic font-medium">{t('platform.reports.noBans')}</p>
                    </div>
                )}
            </div>
          </div>
      </div>
    </div>
  );
};